// Generated by Claude Code
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Building2, LockKeyhole, LogOut, Settings, ShieldCheck } from 'lucide-react'
import { NavCard } from '#components/primitives/components/nav-card.tsx'
import { LanguageSelect } from '#components/composites/language-select.tsx'
import { YnteqWordmark } from '#components/primitives/icons/ynteq-logo.tsx'
import { useUserState } from '#core/state/UserState.ts'
import { UserGeneralSettings } from '#core/state/UserGeneralSettings.ts'
import { useGlobalState } from '#core/state/State.ts'
import User from '#core/entities/auth/users/User.ts'
import { Badge } from '#ynt-design-system/components/badge/index.ts'

/**
 * Landing shown at `/` for a user attached to more than one company. Lists a large
 * card per company they belong to (a single-company user is redirected past this
 * screen); a company this login cannot open carries a badge and opens its unlock screen.
 * The only navigation offered is personal settings, language and logout — the module
 * nav is company-scoped and has no active company here. Super users also
 * get a card into the super-user area.
 *
 * @param path Company-relative path each card opens, keeping the current hash; empty opens the company home.
 * @param search Search each card opens with; omit to keep the current one.
 */
export function CompanySelectLanding({ path = '', search }: { path?: string; search?: Record<string, string> }) {
  const { t } = useTranslation(['home', 'navigation'])
  const userState = useUserState()
  const generalSettings = useGlobalState(UserGeneralSettings, ['language'])
  const companies = User.memberCompanies(userState.user)

  const onLanguageChange = (lang: string) => {
    generalSettings.language = lang
    void generalSettings.save()
  }

  // Labels hide below md so the mobile top bar stays icon-only (no wrapping); the desktop
  // side menu renders at md+ where the labels show. Only one chrome is visible per breakpoint.
  const settingsLink = (
    <Link
      to="/settings/user"
      className="nav-link d-flex align-items-center gap-2 px-3 rounded py-1"
      aria-label={t('navigation:side_menu.settings')}
    >
      <Settings className="flex-shrink-0 h-5 w-5" />
      <span className="fs-6 fw-normal d-none d-md-inline text-nowrap">{t('navigation:side_menu.settings')}</span>
    </Link>
  )
  const logoutLink = (
    <Link
      to="/logout"
      className="nav-link d-flex align-items-center gap-2 px-3 rounded py-1"
      aria-label={t('navigation:side_menu.logout')}
    >
      <LogOut className="flex-shrink-0 h-5 w-5" />
      <span className="fs-6 fw-normal d-none d-md-inline text-nowrap">{t('navigation:side_menu.logout')}</span>
    </Link>
  )

  return (
    <div className="d-flex flex-column flex-md-row vh-100">
      {/* Mobile: slim top bar. The company-scoped bottom nav has no active company here. */}
      <div className="d-flex d-md-none align-items-center border-bottom px-3 py-2 gap-2">
        <YnteqWordmark height={24} style={{ color: 'var(--ynt-brand-ink)' }} />
        <div className="ms-auto d-flex align-items-center gap-1">
          {settingsLink}
          {logoutLink}
          <LanguageSelect style={{ maxWidth: '72px' }} onLanguageChange={onLanguageChange} />
        </div>
      </div>

      {/* Desktop: side menu with only settings, logout and language. */}
      <div className="col-auto d-none d-md-block">
        <ul
          className="d-flex flex-column nav side-nav app-side-nav h-100 border-end py-2 px-0 gap-1"
          style={{ minWidth: '17.5rem' }}
        >
          <div className="d-flex align-items-center px-4 pt-1 pb-2" style={{ minHeight: '3rem' }}>
            <YnteqWordmark height={28} style={{ color: 'var(--ynt-brand-ink)' }} />
          </div>
          <hr className="my-1 w-100" />
          <li className="nav-item px-2 h-8">{settingsLink}</li>
          <li className="mt-auto" />
          <li className="nav-item px-2 h-8">{logoutLink}</li>
          <li className="nav-item">
            <div className="d-flex align-items-center gap-2 px-3 py-2">
              <label className="form-label mb-0 text-secondary">{t('shared.fields.language')}</label>
              <LanguageSelect style={{ maxWidth: '120px' }} onLanguageChange={onLanguageChange} />
            </div>
          </li>
        </ul>
      </div>

      <div className="col vh-100 overflow-y-auto">
        <div className="container py-4 py-md-5">
          <h1 className="h4 fw-bold mb-1">{t('home:selectCompany.title')}</h1>
          <p className="text-muted mb-4">{t('home:selectCompany.body')}</p>
          <div className="row g-3">
            {companies.map((company) => (
              <div key={company.segment} className="col-12 col-md-6 col-lg-4">
                <Link
                  to={'/' + company.segment + '/' + path}
                  search={search ?? true}
                  hash={true}
                  className="text-decoration-none text-reset d-block h-100"
                >
                  <NavCard
                    icon={company.lockReasons.length > 0 ? LockKeyhole : Building2}
                    title={company.name}
                    description={
                      company.lockReasons.length > 0
                        ? company.lockReasons.map((reason) => t(`home:selectCompany.lockReason.${reason}`)).join(' ')
                        : t('home:selectCompany.open')
                    }
                    badge={company.lockReasons.length > 0 && (
                      <Badge type="warning" shape="pill" leadingIcon={LockKeyhole}>
                        {t('home:selectCompany.locked')}
                      </Badge>
                    )}
                  />
                </Link>
              </div>
            ))}
            {userState.isSuperAdmin && (
              <div className="col-12 col-md-6 col-lg-4">
                <Link to="/super-user" className="text-decoration-none text-reset d-block h-100">
                  <NavCard
                    icon={ShieldCheck}
                    title={t('home:noCompany.superUser')}
                    description={t('home:selectCompany.superUserBody')}
                  />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
